/**
 * Content-template resolution for lesson bodies and task hints.
 *
 * Lesson content ships as static JSON, so any concrete date embedded in it
 * eventually ages out — the Excess Stock Return scenario in returns-lesson-3
 * once told the learner to enter 31 Dec 2026, and the Scanned Item sheet's
 * `isTooSoon` check (≥ 3 months) stops accepting that date once "today" is
 * past the end of September 2026. Rather than chase the calendar on every
 * revision, authors write a token and the renderer substitutes a date that
 * is always comfortably past the validator at render time.
 *
 * Supported tokens:
 *   `{{safeExpiryDate}}`       → "30 Apr 2027" (D MMM YYYY)
 *   `{{safeExpiryDateSlash}}`  → "30/04/2027" (DD/MM/YYYY)
 *
 * Both resolve to the same underlying day — the last day of the sixth month
 * after `now`. That gives a 5.5-7-month cushion over the 3-month validator in
 * every month of the year, and lands on a memorable end-of-month anchor the
 * learner can type without second-guessing.
 */

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const

/**
 * Last day of the sixth month after `now`.
 *
 * `new Date(y, m, 0)` returns the last day of month `m - 1`, including
 * January rollovers via the native date normalisation — so passing
 * `now.getMonth() + 7` lands on the last day of six-months-later without
 * special-casing December.
 */
export function safeExpiryDate(now: Date = new Date()): Date {
  return new Date(now.getFullYear(), now.getMonth() + 7, 0)
}

export function formatSafeExpiryDate(now: Date = new Date()): string {
  const d = safeExpiryDate(now)
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`
}

export function formatSafeExpiryDateSlash(now: Date = new Date()): string {
  const d = safeExpiryDate(now)
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  return `${dd}/${mm}/${d.getFullYear()}`
}

/**
 * Substitute content tokens in a body or hint string. Unknown tokens are
 * left as-is so an unresolved placeholder is visible rather than silently
 * dropped — easier to spot in a review than a missing word would be.
 */
export function resolveContentTemplates(text: string, now: Date = new Date()): string {
  if (!text.includes('{{')) return text
  return text
    .replaceAll('{{safeExpiryDate}}', formatSafeExpiryDate(now))
    .replaceAll('{{safeExpiryDateSlash}}', formatSafeExpiryDateSlash(now))
}
